import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { COMPANIES } from "@shared/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Facebook, Upload, Image, Video, X, Eye, CheckCircle, XCircle,
  Clock, Send, Edit, Trash2, ExternalLink, AlertCircle, Plus, ChevronLeft, ChevronRight, Download
} from "lucide-react";

type MediaItem = { url: string; type: "image" | "video"; name: string };
type FBPost = {
  id: number;
  companyName: string;
  submitterName: string;
  siteName: string;
  content: string;
  mediaUrls: string[];
  mediaTypes: string[];
  status: string;
  createdAt: Date | string;
  rejectReason?: string | null;
  postedAt?: Date | string | null;
};

const STATUS_MAP: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending:  { label: "待審核", color: "bg-amber-100 text-amber-700 border-amber-300",  icon: <Clock className="w-3 h-3" /> },
  approved: { label: "已核准", color: "bg-blue-100 text-blue-700 border-blue-300",    icon: <CheckCircle className="w-3 h-3" /> },
  rejected: { label: "已退回", color: "bg-red-100 text-red-700 border-red-300",       icon: <XCircle className="w-3 h-3" /> },
  posted:   { label: "已PO文", color: "bg-green-100 text-green-700 border-green-300", icon: <Facebook className="w-3 h-3" /> },
};

function downloadFile(url: string, filename: string) {
  fetch(url)
    .then(res => res.blob())
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    })
    .catch(() => {
      // fallback: open in new tab
      window.open(url, '_blank');
    });
}

function MediaPreview({ urls, types, names }: { urls: string[]; types: string[]; names?: string[] }) {
  const [current, setCurrent] = useState(0);
  if (!urls.length) return null;
  const currentName = names?.[current] || `media-${current + 1}`;
  return (
    <div className="space-y-2">
      <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
        {types[current] === "video" ? (
          <video src={urls[current]} controls className="w-full h-full object-contain" />
        ) : (
          <img src={urls[current]} alt="" className="w-full h-full object-contain" />
        )}
        {/* 下載當前檔案按鈕 */}
        <button
          onClick={() => downloadFile(urls[current], currentName)}
          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors"
          title="下載此檔案">
          <Download className="w-4 h-4" />
        </button>
        {urls.length > 1 && (
          <>
            <button onClick={() => setCurrent(p => (p - 1 + urls.length) % urls.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setCurrent(p => (p + 1) % urls.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70" style={{ top: 'calc(50% + 20px)' }}>
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {urls.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-white" : "bg-white/50"}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {/* 縮圖列表 + 個別下載 */}
      {urls.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {urls.map((url, i) => (
            <div key={i} className="relative group">
              <div
                onClick={() => setCurrent(i)}
                className={`w-16 h-16 rounded overflow-hidden cursor-pointer border-2 transition-all ${
                  i === current ? 'border-blue-500' : 'border-transparent'
                }`}>
                {types[i] === 'video' ? (
                  <video src={url} className="w-full h-full object-cover" />
                ) : (
                  <img src={url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <button
                onClick={() => downloadFile(url, names?.[i] || `media-${i + 1}`)}
                className="absolute -top-1 -right-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                title="下載">
                <Download className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FBPostModule() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const utils = trpc.useUtils();

  // 列表狀態
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "rejected" | "posted">("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const { data: listData, isLoading: listLoading } = trpc.fbPost.list.useQuery({
    page, pageSize: PAGE_SIZE, status: statusFilter,
  });

  // 提交表單狀態
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [editingPost, setEditingPost] = useState<FBPost | null>(null);
  const [form, setForm] = useState({
    companyName: "", submitterName: user?.name || "", siteName: "", content: "",
  });
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 詳情/審核對話框
  const [viewPost, setViewPost] = useState<FBPost | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);

  const uploadMediaMutation = trpc.fbPost.uploadMedia.useMutation();
  const submitMutation = trpc.fbPost.submit.useMutation({
    onSuccess: () => {
      toast.success("已成功提交 FB 貼文申請，系統已通知管理者審核！");
      setShowSubmitDialog(false);
      resetForm();
      utils.fbPost.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const updateMutation = trpc.fbPost.update.useMutation({
    onSuccess: () => {
      toast.success("已更新貼文資料");
      setShowSubmitDialog(false);
      resetForm();
      utils.fbPost.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const reviewMutation = trpc.fbPost.review.useMutation({
    onSuccess: (_, vars) => {
      const msg = vars.action === "approve" ? "已核准" : vars.action === "reject" ? "已退回" : "已標記為已PO文";
      toast.success(msg);
      setViewPost(null);
      setShowRejectInput(false);
      setRejectReason("");
      utils.fbPost.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteMutation = trpc.fbPost.delete.useMutation({
    onSuccess: () => {
      toast.success("已刪除貼文申請");
      utils.fbPost.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  function resetForm() {
    setForm({ companyName: "", submitterName: user?.name || "", siteName: "", content: "" });
    setMediaItems([]);
    setEditingPost(null);
  }

  function openEdit(post: FBPost) {
    setEditingPost(post);
    setForm({
      companyName: post.companyName,
      submitterName: post.submitterName,
      siteName: post.siteName,
      content: post.content,
    });
    setMediaItems(post.mediaUrls.map((url, i) => ({
      url, type: (post.mediaTypes[i] as "image" | "video") || "image", name: `media-${i + 1}`,
    })));
    setShowSubmitDialog(true);
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (mediaItems.length + files.length > 8) {
      toast.error("最多只能上傳 8 個媒體檔案");
      return;
    }
    setUploading(true);
    for (const file of files) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) { toast.error(`不支援的檔案類型：${file.name}`); continue; }
      if (file.size > 50 * 1024 * 1024) { toast.error(`檔案過大（最大 50MB）：${file.name}`); continue; }
      try {
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res((reader.result as string).split(",")[1]);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const result = await uploadMediaMutation.mutateAsync({
          fileBase64: base64, fileName: file.name, mimeType: file.type,
        });
        setMediaItems(prev => [...prev, { url: result.url, type: isVideo ? "video" : "image", name: file.name }]);
      } catch { toast.error(`上傳失敗：${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSubmit() {
    if (!form.companyName.trim()) { toast.error("請填寫公司名稱"); return; }
    if (!form.submitterName.trim()) { toast.error("請填寫提供者姓名"); return; }
    if (!form.siteName.trim()) { toast.error("請填寫案場名稱"); return; }
    if (!form.content.trim()) { toast.error("請填寫 PO 文內容"); return; }
    if (mediaItems.length === 0) { toast.error("請至少上傳一張圖片或影片"); return; }
    setSubmitting(true);
    const payload = {
      ...form,
      mediaUrls: mediaItems.map(m => m.url),
      mediaTypes: mediaItems.map(m => m.type) as ("image" | "video")[],
    };
    try {
      if (editingPost) {
        await updateMutation.mutateAsync({ id: editingPost.id, ...payload });
      } else {
        await submitMutation.mutateAsync(payload);
      }
    } finally { setSubmitting(false); }
  }

  const totalPages = Math.ceil((listData?.total || 0) / PAGE_SIZE);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            {/* 光環動畫 */}
            <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-blue-400 to-blue-600 opacity-30 animate-pulse blur-sm -z-10" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">FB 貼文模組</h1>
            <p className="text-sm text-muted-foreground">提交活動相片與資料，由管理者審核後 PO 上集團 FB</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowSubmitDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
          <Plus className="w-4 h-4" />
          新增貼文申請
        </Button>
      </div>

      {/* FB 連結提示 */}
      <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-800">
        <CardContent className="py-3 px-4 flex items-center gap-3">
          <Facebook className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="text-sm text-blue-700 dark:text-blue-300">
            審核通過後，管理者將 PO 文至：
          </span>
          <a href="https://www.facebook.com/cesst24650448" target="_blank" rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1">
            誠鷹集團官方 FB <ExternalLink className="w-3 h-3" />
          </a>
        </CardContent>
      </Card>

      {/* 篩選 & 統計 */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">篩選狀態：</span>
        {(["all", "pending", "approved", "rejected", "posted"] as const).map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              statusFilter === s
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-background text-muted-foreground border-border hover:border-blue-400"
            }`}>
            {s === "all" ? "全部" : STATUS_MAP[s]?.label}
            {s === "all" && listData && <span className="ml-1 opacity-70">({listData.total})</span>}
          </button>
        ))}
      </div>

      {/* 貼文列表 */}
      {listLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : !listData?.items.length ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Facebook className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">尚無貼文申請</p>
            <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowSubmitDialog(true); }}>
              立即新增第一筆申請
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {listData.items.map((post: FBPost) => {
            const st = STATUS_MAP[post.status] || STATUS_MAP.pending;
            return (
              <Card key={post.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4 px-5">
                  <div className="flex items-start gap-4">
                    {/* 縮圖 */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
                      {post.mediaUrls[0] ? (
                        post.mediaTypes[0] === "video" ? (
                          <video src={post.mediaUrls[0]} className="w-full h-full object-cover" />
                        ) : (
                          <img src={post.mediaUrls[0]} alt="" className="w-full h-full object-cover" />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    {/* 資訊 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                          {st.icon}{st.label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {post.companyName} · {post.submitterName} · {post.siteName}
                        </span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {new Date(post.createdAt).toLocaleDateString("zh-TW")}
                        </span>
                      </div>
                      <p className="text-sm text-foreground line-clamp-2">{post.content}</p>
                      {post.status === "rejected" && post.rejectReason && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />退回原因：{post.rejectReason}
                        </p>
                      )}
                      {post.status === "posted" && post.postedAt && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          已於 {new Date(post.postedAt).toLocaleDateString("zh-TW")} PO 文
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">
                          {post.mediaUrls.length} 個媒體檔案
                        </span>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1"
                          onClick={() => setViewPost(post as FBPost)}>
                          <Eye className="w-3 h-3" />查看
                        </Button>
                        {(post.status === "pending" || isSuperAdmin) && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1"
                            onClick={() => openEdit(post as FBPost)}>
                            <Edit className="w-3 h-3" />編輯
                          </Button>
                        )}
                        {(isSuperAdmin || post.status === "pending") && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-red-500 hover:text-red-600"
                            onClick={() => {
                              if (confirm("確定要刪除這筆申請嗎？")) deleteMutation.mutate({ id: post.id });
                            }}>
                            <Trash2 className="w-3 h-3" />刪除
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground self-center">
            第 {page} / {totalPages} 頁
          </span>
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ===== 提交/編輯 Dialog ===== */}
      <Dialog open={showSubmitDialog} onOpenChange={v => { if (!v) { setShowSubmitDialog(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Facebook className="w-5 h-5 text-blue-600" />
              {editingPost ? "編輯 FB 貼文申請" : "新增 FB 貼文申請"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>提供者公司名 <span className="text-red-500">*</span></Label>
                <Select value={form.companyName} onValueChange={v => setForm(f => ({ ...f, companyName: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="請選擇公司" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANIES.filter(c => c.id !== 0).map(c => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>提供者姓名 <span className="text-red-500">*</span></Label>
                <Input placeholder="請輸入姓名"
                  value={form.submitterName}
                  onChange={e => setForm(f => ({ ...f, submitterName: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>案場名稱 <span className="text-red-500">*</span></Label>
              <Input placeholder="請輸入辦理活動的案場名稱"
                value={form.siteName}
                onChange={e => setForm(f => ({ ...f, siteName: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>PO 文內容 <span className="text-red-500">*</span></Label>
              <Textarea placeholder="請輸入要發布在 FB 上的文字內容，包含活動說明、時間、地點等資訊..."
                rows={5} value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
            </div>
            {/* 媒體上傳 */}
            <div className="space-y-2">
              <Label>
                活動相片 / 影片 <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground ml-2">（1～8 個，至少一張）</span>
              </Label>
              <div
                className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  點擊或拖曳上傳圖片 / 影片
                </p>
                <p className="text-xs text-muted-foreground mt-1">支援 JPG、PNG、WebP、MP4、MOV，單檔最大 50MB</p>
              </div>
              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*"
                className="hidden" onChange={handleFileSelect} />
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  上傳中...
                </div>
              )}
              {mediaItems.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {mediaItems.map((m, i) => (
                    <div key={i} className="relative group rounded-lg overflow-hidden aspect-square bg-muted">
                      {m.type === "video" ? (
                        <video src={m.url} className="w-full h-full object-cover" />
                      ) : (
                        <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button onClick={() => setMediaItems(prev => prev.filter((_, idx) => idx !== i))}
                          className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="absolute bottom-1 left-1">
                        {m.type === "video"
                          ? <Video className="w-3 h-3 text-white drop-shadow" />
                          : <Image className="w-3 h-3 text-white drop-shadow" />}
                      </div>
                    </div>
                  ))}
                  {mediaItems.length < 8 && (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center hover:border-blue-400 hover:bg-blue-50/30 transition-all">
                      <Plus className="w-5 h-5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSubmitDialog(false); resetForm(); }}>取消</Button>
            <Button onClick={handleSubmit} disabled={submitting || uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />提交中...</>
                : <><Send className="w-4 h-4" />{editingPost ? "更新申請" : "提交申請"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== 查看/審核 Dialog ===== */}
      <Dialog open={!!viewPost} onOpenChange={v => { if (!v) { setViewPost(null); setShowRejectInput(false); setRejectReason(""); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewPost && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />貼文申請詳情
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ml-2 ${STATUS_MAP[viewPost.status]?.color}`}>
                    {STATUS_MAP[viewPost.status]?.icon}
                    {STATUS_MAP[viewPost.status]?.label}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">公司名稱</span><p className="font-medium">{viewPost.companyName}</p></div>
                  <div><span className="text-muted-foreground">提供者</span><p className="font-medium">{viewPost.submitterName}</p></div>
                  <div><span className="text-muted-foreground">案場名稱</span><p className="font-medium">{viewPost.siteName}</p></div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">PO 文內容</p>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm whitespace-pre-wrap">{viewPost.content}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">媒體檔案（{viewPost.mediaUrls.length} 個）</p>
                  <MediaPreview urls={viewPost.mediaUrls} types={viewPost.mediaTypes} />
                </div>
                {viewPost.status === "rejected" && viewPost.rejectReason && (
                  <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-700 dark:text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />退回原因
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-1">{viewPost.rejectReason}</p>
                  </div>
                )}
                {/* 退回原因輸入 */}
                {showRejectInput && (
                  <div className="space-y-2">
                    <Label>退回原因 <span className="text-red-500">*</span></Label>
                    <Textarea placeholder="請說明退回原因，以便提交者修改..." rows={3}
                      value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
                  </div>
                )}
              </div>
              {/* 審核按鈕（僅 superadmin） */}
              {isSuperAdmin && (
                <DialogFooter className="flex-wrap gap-2">
                  {viewPost.status === "pending" && !showRejectInput && (
                    <>
                      <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                        onClick={() => setShowRejectInput(true)}>
                        <XCircle className="w-4 h-4" />退回
                      </Button>
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                        onClick={() => reviewMutation.mutate({ id: viewPost.id, action: "approve" })}>
                        <CheckCircle className="w-4 h-4" />核准
                      </Button>
                    </>
                  )}
                  {showRejectInput && (
                    <>
                      <Button variant="outline" onClick={() => { setShowRejectInput(false); setRejectReason(""); }}>取消</Button>
                      <Button className="bg-red-600 hover:bg-red-700 text-white gap-1"
                        disabled={!rejectReason.trim()}
                        onClick={() => reviewMutation.mutate({ id: viewPost.id, action: "reject", rejectReason })}>
                        <XCircle className="w-4 h-4" />確認退回
                      </Button>
                    </>
                  )}
                  {viewPost.status === "approved" && (
                    <Button className="bg-green-600 hover:bg-green-700 text-white gap-1"
                      onClick={() => reviewMutation.mutate({ id: viewPost.id, action: "markPosted" })}>
                      <Facebook className="w-4 h-4" />標記為已 PO 文
                    </Button>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
